<script lang="ts">
  import { Link } from "@inertiajs/svelte";
  import type { Trip } from "../../lib/types";
  import { formatCurrency } from "../../lib/utils";

  let { trip }: { trip: Trip } = $props();

  const statusStyles: Record<string, { bg: string; color: string; label: string }> = {
    planning:  { bg: "color-mix(in srgb, var(--surface-hover) 90%, transparent)", color: "var(--text-secondary)", label: "계획중" },
    active:    { bg: "rgba(16,185,129,0.15)",    color: "#34d399",              label: "진행중" },
    completed: { bg: "rgba(32,178,170,0.15)",    color: "#20b2aa",              label: "완료"   },
    cancelled: { bg: "rgba(239,68,68,0.12)",     color: "#f87171",              label: "취소됨" },
  };

  function formatDateRange(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    return `${startDate.toLocaleDateString("ko-KR", options)} - ${endDate.toLocaleDateString("ko-KR", options)}`;
  }

  function getProgressColor(progress: number): string {
    if (progress < 50) return "#20b2aa";
    if (progress < 80) return "#f59e0b";
    return "#ef4444";
  }

  const status = $derived(statusStyles[trip.status] ?? statusStyles.planning);
</script>

<Link
  href={`/trips/${trip.id}`}
  class="card card-hover block no-underline"
>
  <!-- Header -->
  <div class="flex items-start justify-between mb-3">
    <div class="min-w-0 pr-2">
      <h3 class="text-base font-semibold truncate" style="color: var(--text-primary);">{trip.name}</h3>
      <p class="text-sm mt-0.5" style="color: var(--text-secondary);">{trip.destination}</p>
    </div>
    <span
      class="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium"
      style="background: {status.bg}; color: {status.color};"
    >
      {status.label}
    </span>
  </div>

  <!-- Date + Duration -->
  <div class="flex items-center text-sm mb-4" style="color: var(--text-muted);">
    <svg class="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
    <span>{formatDateRange(trip.startDate, trip.endDate)}</span>
    <span class="mx-2" style="color: var(--border-bright);">·</span>
    <span>{trip.durationDays}일</span>
  </div>

  <!-- Budget Progress -->
  <div>
    <div class="flex justify-between text-xs mb-1.5">
      <span style="color: var(--text-muted);">예산 사용</span>
      <span class="font-medium" style="color: var(--text-secondary);">
        ₩{formatCurrency(trip.spentCents)} / ₩{formatCurrency(trip.budgetCents)}
      </span>
    </div>
    <div class="w-full h-1.5 rounded-full" style="background: color-mix(in srgb, var(--surface-hover) 90%, transparent);">
      <div
        class="h-1.5 rounded-full transition-all duration-500"
        style="width: {Math.min(trip.budgetProgress, 100)}%; background: {getProgressColor(trip.budgetProgress)}; box-shadow: 0 0 8px {getProgressColor(trip.budgetProgress)}60;"
      ></div>
    </div>
    <div class="text-right text-xs mt-1" style="color: var(--text-muted);">
      {trip.budgetProgress.toFixed(0)}% 사용
    </div>
  </div>
</Link>
